import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

/** Where the spinner sits inside its region, in px from the region's top-left; null = use the region's centre. */
export interface SpinnerPlacement {
  top: number | null;
  left: number | null;
  /**
   * `top`/`left` are viewport coordinates and the spinner is `position: fixed`:
   * the anchor lies outside the region (#1561 B4, three dashboard panels share
   * one spinner), so the region's own clipping must not hide it.
   */
  fixed?: boolean;
}

const CENTRED: SpinnerPlacement = { top: null, left: null };

function clipToScrollAncestors(el: HTMLElement, top: number, bottom: number): [number, number] {
  let t = top;
  let b = bottom;
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'visible') continue;
    const r = node.getBoundingClientRect();
    t = Math.max(t, r.top);
    b = Math.min(b, r.bottom);
  }
  return [t, b];
}

function measure(region: HTMLElement, anchor: HTMLElement | null): SpinnerPlacement {
  const rect = region.getBoundingClientRect();
  if (rect.height === 0 && rect.width === 0) return CENTRED;
  if (anchor) {
    const a = anchor.getBoundingClientRect();
    if (a.height > 0 || a.width > 0) {
      if (!region.contains(anchor)) {
        return { fixed: true, top: Math.round(a.top + a.height / 2), left: Math.round(a.left + a.width / 2) };
      }
      return {
        top: Math.round(a.top + a.height / 2 - rect.top),
        left: Math.round(a.left + a.width / 2 - rect.left),
      };
    }
  }
  const [top, bottom] = clipToScrollAncestors(region, Math.max(rect.top, 0), Math.min(rect.bottom, window.innerHeight));
  if (bottom <= top) return CENTRED;
  return { top: Math.round((top + bottom) / 2 - rect.top), left: null };
}

/**
 * Centre the spinner in the VISIBLE part of its region (clipped to the viewport
 * and to scrolling ancestors), or on `anchorRef`'s element when given (#1561
 * W2): a 4,000 px skeleton must not put the spinner off-screen.
 */
export function useSpinnerPlacement(
  regionRef: RefObject<HTMLElement | null>,
  anchorRef: RefObject<HTMLElement | null> | undefined,
  enabled: boolean,
): SpinnerPlacement {
  const [placement, setPlacement] = useState<SpinnerPlacement>(CENTRED);
  // What the last setPlacement committed. Compared BEFORE calling setState: a
  // functional `setPlacement(prev => prev)` is not eagerly bailed out by React
  // (it schedules a render), so each unchanged frame re-measure warned
  // "update not wrapped in act" in tests (#1561-e2).
  const lastRef = useRef<SpinnerPlacement>(CENTRED);

  useLayoutEffect(() => {
    const region = regionRef.current;
    if (!enabled || !region) return;
    const update = () => {
      const next = measure(region, anchorRef?.current ?? null);
      const prev = lastRef.current;
      if (prev.top === next.top && prev.left === next.left && prev.fixed === next.fixed) return;
      lastRef.current = next;
      setPlacement(next);
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    ro?.observe(region);
    if (anchorRef?.current) ro?.observe(anchorRef.current);
    // #1561-e: a ResizeObserver never fires when content above the anchor
    // (an image loading, a banner appearing) moves it without resizing it, so
    // re-measure every frame while the spinner is shown. update() sets state
    // only when top/left/fixed change (compared against lastRef, not inside
    // the setState updater).
    let frame = 0;
    const tick = () => {
      update();
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [regionRef, anchorRef, enabled]);

  return placement;
}
